import { FieldArray, FormikProps, useFormikContext } from 'formik'
import { addMonths, addQuarters, addYears } from 'date-fns'
import {
  ButtonV2,
  DatePickerV2,
  RadioButtonGroupMapping,
  NoteV2,
  RadioButtonsV2,
  TextInputV4,
  labraUIDateFormat,
} from '@labrav/react-components'
import { isNonEmptyNumber, numberFormatWithPrecision } from '@labrav/common'
import { get, isUndefined } from 'lodash'
import { Installments, ScheduleType } from '../../../types/flyout'
import { useStyles } from './PaymentScheduleV2.styles'
import { Typography, useTheme } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getRandom } from '../../utils/helperFunctions'
import clsx from 'clsx'

import { getScheduleTypeOptions } from '../../utils/productCreationUtil'
import { DetailInfo } from '../DetailRow/DetailRow'
import { getCurrencySymbol } from '../../utils/getCurrencySymbol'
import { getDaysFromServiceLength } from '../../utils/getDaysFromServiceLength'
import { getContractDurationInMonths } from '../../../utils/containerOfferHelperFunctions'
import { clampScheduledInstallmentCount } from '../../utils/constants'
import {
  buildRenewalEqualScheduleRows,
  clampDayOfMonth,
  dayFromIsoDate,
  emptyRenewalInstallment,
  getInstallmentPercentages,
  isRenewalMonthsAfterFilled,
  labelToSchedule,
  renewalPercentagesSumTo100,
  scheduleLabel,
} from './renewalSchedule'
import { parsePercentInput } from '../../utils/renewalDetailsFormat'
import { useCustomRenewalSync } from './useCustomRenewalSync'
import {
  awsMonthlyInstallmentsBannerMessage,
  RENEWAL_DAY_OF_MONTH_TOOLTIP,
  RENEWAL_MONTHS_AFTER_REQUIRED,
  RENEWAL_PERCENT_SUM_ERROR,
} from '../../utils/messagesContants'

type PaymentScheduleFormValues = {
  installments?: Installments[]
  flexiblePaymentType?: ScheduleType
  serviceLength?: number
  firstInstallmentDate?: string | null
  currency?: string
  containerOfferServiceLength?: string | number
  agreementStartDate?: string | null
  subscriptionEndDate?: string | null
} & Record<string, unknown>

const EMPTY_INSTALLMENT = {
  amount: null,
  paymentDate: '',
  currency: 'USD',
} as const

const nextMonthSameDay = (iso?: string) => {
  if (!iso) return ''
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return ''
  return addMonths(parsed, 1).toISOString().split('T')[0]
}

interface CustomColumnInstallment {
  keyName: string
  header: string
}
interface PaymentScheduleV2Props {
  formik: FormikProps<Record<string, unknown>>
  subHeading?: React.ReactNode
  addButtonName: string
  buttonCounterName: string
  shouldShowTotal?: boolean
  installmentsLimit: number
  sectionDescription?: string
  sectionHeading?: string
  tcvKey?: string
  tcv?: number
  fixTcvView?: boolean
  handleChangeInstallmentsAmount?: (installents: Installments[]) => void
  customColumns?: CustomColumnInstallment[]
  totalOtherAmount?: number
  updatedRandomKey?: number
  validate?: boolean
  hideOuterBorder?: boolean
  /** Figma renewal installments: no TCV field — only first installment date */
  hideTcvInput?: boolean
  /** Figma renewal installments: auto-seeded, no Generate CTA */
  hideGenerateButton?: boolean
  /**
   * renewal: monthsAfter / dayOfMonth / % columns on installmentsField
   * (default Formik keys: renewalInstallments + renewalFlexiblePaymentType)
   */
  mode?: 'standard' | 'renewal'
  installmentsField?: string
  scheduleTypeField?: string
  dayOfMonthField?: string
  priceUpliftPercent?: number
}

type InstallmentRowProps = {
  index: number
  installment: Installments
  randomKey: number
  amountIsError: boolean
  amountErrorText: string
  dateShowError: boolean
  dateErrorText: string
  showDelete: boolean
  columnDivision: number
  customColumns: CustomColumnInstallment[]
  classes: ReturnType<typeof useStyles>['classes']
  formik: FormikProps<Record<string, unknown>>
  onAmountChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void
  handleBlur: FormikProps<PaymentScheduleFormValues>['handleBlur']
  onRemove: (index: number) => void
  validate?: boolean
}

const InstallmentRow = memo(function InstallmentRow({
  index,
  installment,
  randomKey,
  amountIsError,
  amountErrorText,
  dateShowError,
  dateErrorText,
  showDelete,
  customColumns,
  classes,
  formik,
  onAmountChange,
  handleBlur,
  onRemove,
  validate = false,
}: InstallmentRowProps) {
  const handleAmount = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onAmountChange(e, index),
    [onAmountChange, index]
  )

  const handleRemove = useCallback(() => onRemove(index), [onRemove, index])

  return (
    <div className={classes.resourceRow} data-testid="installments-row">
      <div className={classes.resourcesContainer}>
        {customColumns.map((customColumnItem, customColumnItemIndex) => (
          <div
            key={`${customColumnItem.keyName}-${customColumnItemIndex}`}
            className={clsx(
              classes.columnTextValue,
              classes.columnFlex,
              !customColumnItemIndex
                ? classes.textAlignLeft
                : classes.textAlignRight
            )}
            data-testid="custom-column-value"
          >
            {customColumnItem?.keyName === 'index'
              ? index + 1
              : get(installment, customColumnItem?.keyName)}
          </div>
        ))}
        <div
          className={clsx(classes.columnFlex, classes.installmentField)}
          key={`installments-${randomKey}-${index}-amount`}
        >
          <TextInputV4
            keyName={`installments.${index}.amount`}
            title="Enter amount"
            type="number"
            placeHolder=""
            value={installment?.amount ?? ''}
            updatedValue={installment?.amount ?? ''}
            isError={amountIsError}
            errorText={amountErrorText}
            onChange={handleAmount}
            isReadOnly={false}
            isRequired={false}
            showSpinner={false}
            onBlur={handleBlur}
            keyToPrevent={['+', '-', 'e']}
          />
        </div>
        <div
          className={clsx(classes.columnFlex, classes.installmentField)}
          key={`installments-${randomKey}-${index}-paymentDate`}
        >
          <DatePickerV2
            size="small"
            format={labraUIDateFormat}
            views={['year', 'month', 'day']}
            field={{
              title: 'Select a date',
              humanReadableKey: 'Select a date',
              placeHolder: 'Select a date',
              id: `installments[${index}].paymentDate`,
              isRequired: false,
              isReadOnly: false,
              isCustom: false,
              defaultValue: installment.paymentDate,
              dataType: 'date',
              minDate: 'today',
            }}
            fieldId={`installments.${index}.paymentDate`}
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            showErrorHelper={dateShowError}
            errorHelperText={dateErrorText}
            validate={validate}
          />
        </div>
      </div>
      {showDelete ? (
        <div className={classes.trashIcon}>
          <FontAwesomeIcon
            icon={faTrash}
            className={classes.trashIconSize}
            data-testid="trash-installments"
            onClick={handleRemove}
          />
        </div>
      ) : null}
    </div>
  )
})

type RenewalScheduleModeProps = {
  formik: FormikProps<Record<string, unknown>>
  addButtonName: string
  buttonCounterName: string
  installmentsLimit: number
  shouldShowTotal: boolean
  hideOuterBorder: boolean
  installmentsField: string
  scheduleTypeField: string
  dayOfMonthField: string
  priceUpliftPercent: number
  tcv: number
}

const RenewalScheduleMode = ({
  addButtonName,
  buttonCounterName,
  installmentsLimit,
  shouldShowTotal,
  hideOuterBorder,
  installmentsField,
  scheduleTypeField,
  dayOfMonthField,
  priceUpliftPercent,
  tcv,
}: RenewalScheduleModeProps) => {
  const columnDivision = 98 / 3
  const { classes } = useStyles({ columnDivision })
  const theme = useTheme()
  const { values, setFieldValue } = useFormikContext() as FormikProps<
    Record<string, unknown>
  >

  const scheduleType =
    (get(values, scheduleTypeField) as ScheduleType | undefined) ??
    (values.flexiblePaymentType as ScheduleType | undefined) ??
    ScheduleType.QUARTERLY
  const isCustom = scheduleType === ScheduleType.CUSTOM
  const dayOfMonth = clampDayOfMonth(Number(get(values, dayOfMonthField)) || 1)
  const currency = (values.currency as string) || 'USD'
  const serviceLength = Number(values.serviceLength) || 12
  const installments = (get(values, installmentsField) as Installments[]) || []

  useCustomRenewalSync({
    renewalInstallments: installments,
    scheduleTypeField,
    installmentsField,
    dayOfMonthField,
    dayOfMonth,
    currency,
    tcv,
  })

  const scheduleTypeOptions = useMemo(() => getScheduleTypeOptions(false), [])
  // Empty until first seed — avoids skipping auto-fill when Formik already has stale rows
  const seedKeyRef = useRef('')
  const [daySyncKey, setDaySyncKey] = useState(getRandom())
  const renewalRowsRef = useRef(installments)
  renewalRowsRef.current = installments

  const applyDayToRows = useCallback(
    (day: number) => {
      void setFieldValue(dayOfMonthField, day, false)
      const rows = renewalRowsRef.current
      if (!rows.length) return
      void setFieldValue(
        installmentsField,
        rows.map(row => ({ ...row, dayOfMonth: day })),
        false
      )
      setDaySyncKey(getRandom())
    },
    [dayOfMonthField, installmentsField, setFieldValue]
  )

  const buildAutoRows = useCallback(
    (type: ScheduleType) =>
      buildRenewalEqualScheduleRows({
        scheduleType: type,
        serviceLengthMonths: serviceLength,
        dayOfMonth,
        tcv,
        currency,
      }),
    [currency, dayOfMonth, serviceLength, tcv]
  )

  // Default schedule type once
  useEffect(() => {
    if (get(values, scheduleTypeField)) return
    const initialSchedule =
      values.flexiblePaymentType || ScheduleType.QUARTERLY
    void setFieldValue(scheduleTypeField, initialSchedule, false)
  }, [scheduleTypeField, setFieldValue, values])

  // Seed an empty renewal schedule; Generate explicitly replaces populated rows.
  useEffect(() => {
    if (isCustom) {
      seedKeyRef.current = ''
      return
    }
    const mainInstallments = (values.installments as Installments[]) || []
    if (mainInstallments.length > 0) return
    const key = `${scheduleType}|${serviceLength}`
    if (seedKeyRef.current === key) return
    seedKeyRef.current = key
    if (installments.length > 0) return
    void setFieldValue(installmentsField, buildAutoRows(scheduleType), false)
  }, [
    buildAutoRows,
    installmentsField,
    installments.length,
    isCustom,
    scheduleType,
    serviceLength,
    setFieldValue,
    values.installments,
  ])

  // Hydrate dayOfMonth from API rows once when empty
  useEffect(() => {
    if (get(values, dayOfMonthField) != null) return
    const fromRow = installments[0]?.dayOfMonth
    if (fromRow != null) {
      void setFieldValue(dayOfMonthField, fromRow, false)
    }
  }, [dayOfMonthField, installments, setFieldValue, values])

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = labelToSchedule(event.target.value)
    void setFieldValue(scheduleTypeField, next, false)
    if (next === ScheduleType.CUSTOM) {
      seedKeyRef.current = ''
      if (installments.length === 0) {
        void setFieldValue(
          installmentsField,
          [emptyRenewalInstallment(dayOfMonth, currency)],
          false
        )
      }
      return
    }
    // Immediate auto-fill from frequency; rows stay editable afterward
    const rows = buildAutoRows(next)
    seedKeyRef.current = `${next}|${serviceLength}`
    void setFieldValue(installmentsField, rows, false)
  }

  const updateRow = async (index: number, patch: Partial<Installments>) => {
    const next = installments.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    )
    // Keep amount in sync with percentage when % changes
    if (patch.percentage != null && isNonEmptyNumber(patch.percentage)) {
      next[index] = {
        ...next[index],
        amount:
          Math.round(((tcv * Number(patch.percentage)) / 100) * 100) / 100,
      }
    }
    await setFieldValue(installmentsField, next, false)
  }

  const remaining = Math.max(0, installmentsLimit - installments.length)
  const showDelete = installments.length > 1
  const percentSumError = renewalPercentagesSumTo100(installments)
    ? ''
    : RENEWAL_PERCENT_SUM_ERROR

  return (
    <div
      className={classes.installmentsWrapper}
      data-testid="renewal-installment-section"
    >
      <div className={classes.radioSelection}>
        <RadioButtonsV2
          checkedValue={{
            key: scheduleLabel(scheduleType),
            value: scheduleLabel(scheduleType),
          }}
          name={scheduleTypeField}
          radioButtonMappings={scheduleTypeOptions}
          handleChange={handleSelection}
          gapBetween={theme.spacing(2.5)}
        />
      </div>

      {!isCustom && (
        <div className={classes.dayOfMonthField}>
          <TextInputV4
            keyName={dayOfMonthField}
            title="Day of month (Optional)"
            type="number"
            placeHolder=""
            value={dayOfMonth}
            updatedValue={dayOfMonth}
            onChange={e => {
              applyDayToRows(clampDayOfMonth(Number(e.target.value)))
            }}
            isError={false}
            errorText=""
            isReadOnly={false}
            isRequired={false}
            showSpinner={false}
            showInsideInfoIcon
            showInsideInfoIconTooltip={{
              title: '',
              content: RENEWAL_DAY_OF_MONTH_TOOLTIP,
            }}
          />
        </div>
      )}

      <div
        className={clsx(
          classes.root,
          !hideOuterBorder && classes.rootWithBorder,
          classes.elementsGap
        )}
        data-testid="renewal-schedule-table"
      >
        <div className={classes.columnHeaderSection}>
          <div className={classes.titleWraper}>
            <Typography
              className={clsx(classes.sectionText, classes.columnFlex)}
            >
              Months after new contract starts
            </Typography>
            <Typography
              className={clsx(classes.sectionText, classes.columnFlex)}
            >
              Day of the month (Optional)
            </Typography>
            <Typography
              className={clsx(classes.sectionText, classes.columnFlex)}
            >
              Percentage of Total Contract Value
            </Typography>
            {showDelete && <div className={classes.trashHeaderSpacer} />}
          </div>
          <div className={classes.border} />
        </div>

        <FieldArray name={installmentsField}>
          {({ push, remove }) => (
            <div className={classes.installmentsContent}>
              {installments.map((row, index) => (
                <div
                  className={classes.resourceRow}
                  key={`renewal-row-${index}`}
                  data-testid="renewal-installments-row"
                >
                  <div
                    className={clsx(
                      classes.resourcesContainer,
                      (!!percentSumError ||
                        !isRenewalMonthsAfterFilled(row.monthsAfter)) &&
                        classes.resourcesAlignStart
                    )}
                  >
                    <div
                      className={clsx(
                        classes.columnFlex,
                        classes.installmentField
                      )}
                    >
                      <TextInputV4
                        keyName={`${installmentsField}.${index}.monthsAfter`}
                        title=""
                        type="number"
                        placeHolder=""
                        value={row.monthsAfter ?? ''}
                        updatedValue={row.monthsAfter ?? ''}
                        onChange={e => {
                          const raw = e.target.value
                          void updateRow(index, {
                            monthsAfter: raw === '' ? undefined : Number(raw),
                          })
                        }}
                        isError={!isRenewalMonthsAfterFilled(row.monthsAfter)}
                        errorText={
                          isRenewalMonthsAfterFilled(row.monthsAfter)
                            ? ''
                            : RENEWAL_MONTHS_AFTER_REQUIRED
                        }
                        isReadOnly={false}
                        isRequired={false}
                        showSpinner={false}
                        keyToPrevent={['+', '-', 'e']}
                      />
                    </div>
                    <div
                      className={clsx(
                        classes.columnFlex,
                        classes.installmentField
                      )}
                    >
                      <TextInputV4
                        key={`${installmentsField}-${index}-day-${daySyncKey}`}
                        keyName={`${installmentsField}.${index}.dayOfMonth`}
                        title=""
                        type="number"
                        placeHolder=""
                        value={row.dayOfMonth ?? ''}
                        updatedValue={row.dayOfMonth ?? ''}
                        onChange={e => {
                          const raw = e.target.value
                          void updateRow(index, {
                            dayOfMonth:
                              raw === ''
                                ? undefined
                                : clampDayOfMonth(Number(raw)),
                          })
                        }}
                        isError={false}
                        errorText=""
                        isReadOnly={false}
                        isRequired={false}
                        showSpinner={false}
                        keyToPrevent={['+', '-', 'e']}
                      />
                    </div>
                    <div className={classes.columnFlex}>
                      <div className={classes.installmentField}>
                        <TextInputV4
                          keyName={`${installmentsField}.${index}.percentage`}
                          title=""
                          type="text"
                          placeHolder=""
                          value={`${row.percentage ?? 0}%`}
                          updatedValue={`${row.percentage ?? 0}%`}
                          onChange={e => {
                            void updateRow(index, {
                              percentage:
                                parsePercentInput(e.target.value) ?? 0,
                            })
                          }}
                          isError={!!percentSumError}
                          errorText=""
                          isReadOnly={false}
                          isRequired={false}
                          showSpinner={false}
                        />
                      </div>
                      {index === installments.length - 1 && percentSumError ? (
                        <Typography
                          className={classes.percentSumError}
                          data-testid="renewal-percent-sum-error"
                        >
                          {percentSumError}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                  {showDelete ? (
                    <div className={classes.trashIcon}>
                      <FontAwesomeIcon
                        icon={faTrash}
                        className={classes.trashIconSize}
                        data-testid={`renewal-remove-${index}`}
                        onClick={() => remove(index)}
                      />
                    </div>
                  ) : null}
                </div>
              ))}

              {isCustom && remaining > 0 && (
                <div>
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    onClick={() => {
                      const last = installments[installments.length - 1]
                      push({
                        ...emptyRenewalInstallment(
                          last?.dayOfMonth ?? dayOfMonth,
                          currency
                        ),
                        monthsAfter: (last?.monthsAfter ?? 0) + 1,
                        percentage: 0,
                        amount: 0,
                      })
                    }}
                    data-testid="renewal-add-installment"
                  >
                    {addButtonName.startsWith('+')
                      ? addButtonName
                      : `+ ${addButtonName}`}
                  </ButtonV2>
                  <Typography className={classes.buttonSubText}>
                    You can add {remaining} more {buttonCounterName}.
                  </Typography>
                </div>
              )}
            </div>
          )}
        </FieldArray>
      </div>

      {shouldShowTotal && installments.length > 0 && (
        <div
          className={clsx(
            classes.root,
            classes.rootWithBorder,
            classes.lowerWrapper,
            classes.metricsRow
          )}
          data-testid="renewal-installment-metrics"
        >
          <div
            className={classes.metricItem}
            data-testid="renewal-price-uplift"
          >
            <Typography className={classes.sectionText}>
              Price uplift
            </Typography>
            <Typography className={classes.totalText}>
              {priceUpliftPercent}%
            </Typography>
          </div>
          <div
            className={classes.metricItem}
            data-testid="renewal-installment-count"
          >
            <Typography className={classes.sectionText}>
              Number of installments
            </Typography>
            <Typography className={classes.totalText}>
              {installments.length}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}

export const PaymentScheduleV2 = ({
  formik,
  subHeading,
  addButtonName,
  buttonCounterName,
  shouldShowTotal = false,
  installmentsLimit,
  sectionHeading = 'Installment schedule',
  sectionDescription = 'Generate installment plan for the buyer based on total contract value and duration.',
  tcvKey = 'tcv',
  tcv,
  fixTcvView = false,
  handleChangeInstallmentsAmount,
  customColumns = [],
  totalOtherAmount,
  updatedRandomKey,
  validate = false,
  hideOuterBorder = false,
  hideTcvInput = false,
  hideGenerateButton = false,
  mode = 'standard',
  installmentsField = 'installments',
  scheduleTypeField = 'flexiblePaymentType',
  dayOfMonthField = 'renewalDayOfMonth',
  priceUpliftPercent = 0,
}: PaymentScheduleV2Props) => {
  if (mode === 'renewal') {
    const tcvValue = (!isUndefined(tcv) ? tcv : 0) as number
    return (
      <RenewalScheduleMode
        formik={formik}
        addButtonName={addButtonName}
        buttonCounterName={buttonCounterName}
        installmentsLimit={installmentsLimit}
        shouldShowTotal={shouldShowTotal}
        hideOuterBorder={hideOuterBorder}
        installmentsField={installmentsField}
        scheduleTypeField={scheduleTypeField}
        dayOfMonthField={dayOfMonthField}
        priceUpliftPercent={priceUpliftPercent}
        tcv={tcvValue}
      />
    )
  }

  return (
    <StandardPaymentScheduleV2
      formik={formik}
      subHeading={subHeading}
      addButtonName={addButtonName}
      buttonCounterName={buttonCounterName}
      shouldShowTotal={shouldShowTotal}
      installmentsLimit={installmentsLimit}
      sectionHeading={sectionHeading}
      sectionDescription={sectionDescription}
      tcvKey={tcvKey}
      tcv={tcv}
      fixTcvView={fixTcvView}
      handleChangeInstallmentsAmount={handleChangeInstallmentsAmount}
      customColumns={customColumns}
      totalOtherAmount={totalOtherAmount}
      updatedRandomKey={updatedRandomKey}
      validate={validate}
      hideOuterBorder={hideOuterBorder}
      hideTcvInput={hideTcvInput}
      hideGenerateButton={hideGenerateButton}
    />
  )
}

const StandardPaymentScheduleV2 = ({
  formik,
  subHeading,
  addButtonName,
  buttonCounterName,
  shouldShowTotal = false,
  installmentsLimit,
  sectionHeading = 'Installment schedule',
  sectionDescription = 'Generate installment plan for the buyer based on total contract value and duration.',
  tcvKey = 'tcv',
  tcv,
  fixTcvView = false,
  handleChangeInstallmentsAmount,
  customColumns = [],
  totalOtherAmount,
  updatedRandomKey,
  validate = false,
  hideOuterBorder = false,
  hideTcvInput = false,
  hideGenerateButton = false,
}: PaymentScheduleV2Props) => {
  const columnDivision = useMemo(
    () => 98 / (customColumns?.length + 2),
    [customColumns?.length]
  )

  const { classes } = useStyles({ columnDivision })
  const theme = useTheme()
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    handleChange,
    handleBlur,
    errors,
  } = useFormikContext() as FormikProps<PaymentScheduleFormValues>
  const installments = values.installments || ([] as Installments[])

  const tcvValue = (!isUndefined(tcv) ? tcv : get(values, tcvKey, 0)) as number
  const installmentsLength = installments?.length

  const installmentsRef = useRef(installments)
  installmentsRef.current = installments

  const [randomKey, setRandomKey] = useState<number>(getRandom())
  useEffect(() => {
    setRandomKey(updatedRandomKey || getRandom())
  }, [updatedRandomKey])

  const totalAmount = useMemo(
    () =>
      installments.reduce((acc, curr) => {
        const amount = Number(curr.amount)
        if (typeof amount === 'number' && !isNaN(amount)) {
          return acc + amount
        }
        return acc
      }, 0),
    [installments]
  )

  const scheduleTypeOptions: RadioButtonGroupMapping[] = useMemo(
    () => getScheduleTypeOptions(false),
    []
  )

  const checkedValue = useMemo(
    () =>
      values.flexiblePaymentType === ScheduleType.QUARTERLY
        ? 'Quarterly'
        : values.flexiblePaymentType === ScheduleType.MONTHLY
          ? 'Monthly'
          : values.flexiblePaymentType === ScheduleType.YEARLY
            ? 'Yearly'
            : 'Custom',
    [values.flexiblePaymentType]
  )

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionKey = (event.target as HTMLInputElement).value as string
    if (selectedOptionKey === 'Quarterly') {
      setFieldValue('flexiblePaymentType', ScheduleType.QUARTERLY)
    } else if (selectedOptionKey === 'Monthly') {
      setFieldValue('flexiblePaymentType', ScheduleType.MONTHLY)
    } else if (selectedOptionKey === 'Yearly') {
      setFieldValue('flexiblePaymentType', ScheduleType.YEARLY)
    } else if (selectedOptionKey === 'Custom') {
      setFieldValue('flexiblePaymentType', ScheduleType.CUSTOM)
    }
  }

  const calculateNumberOfInstallments = (
    flexiblePaymentType: ScheduleType,
    serviceLength?: number,
    containerServiceLength?: number
  ): number => {
    if (serviceLength) {
      switch (flexiblePaymentType) {
        case ScheduleType.MONTHLY:
          return Math.ceil(serviceLength / 1)
        case ScheduleType.QUARTERLY:
          return Math.ceil(serviceLength / 3)
        case ScheduleType.YEARLY:
          return Math.ceil(serviceLength / 12)
        default:
          return serviceLength
      }
    } else if (containerServiceLength) {
      switch (flexiblePaymentType) {
        case ScheduleType.MONTHLY:
          return Math.ceil(containerServiceLength / 30)
        case ScheduleType.QUARTERLY:
          return Math.ceil(containerServiceLength / 90)
        case ScheduleType.YEARLY:
          return Math.ceil(containerServiceLength / 365)
        default:
          return containerServiceLength
      }
    }
    return 1
  }

  const calculateInstallmentDate = (
    startDate: string,
    flexiblePaymentType: ScheduleType,
    installmentNumber: number
  ): string => {
    let calculatedDate

    switch (flexiblePaymentType) {
      case ScheduleType.MONTHLY:
        calculatedDate = addMonths(new Date(startDate), installmentNumber)
        break
      case ScheduleType.QUARTERLY:
        calculatedDate = addQuarters(new Date(startDate), installmentNumber)
        break
      case ScheduleType.YEARLY:
        calculatedDate = addYears(new Date(startDate), installmentNumber)
        break
      default:
        calculatedDate = new Date(startDate)
    }

    const formattedDate = calculatedDate.toISOString().split('T')[0]
    return formattedDate
  }

  const getInstallmentsBasedOnSchedule = (
    tcv: number,
    durationInMonths: number,
    now: string,
    containerServiceLength?: number
  ) => {
    const quantityOfInstallments = clampScheduledInstallmentCount(
      calculateNumberOfInstallments(
        values.flexiblePaymentType || ScheduleType.CUSTOM,
        durationInMonths,
        containerServiceLength
      )
    )

    const base = Math.floor(tcv / quantityOfInstallments)
    const remainder =
      Math.round((tcv - base * quantityOfInstallments) * 100) / 100

    return Array.from({ length: quantityOfInstallments }).map((_, index) => ({
      amount: base + (index === 0 ? remainder : 0),
      paymentDate: calculateInstallmentDate(
        now || new Date().toISOString(),
        values.flexiblePaymentType || ScheduleType.CUSTOM,
        index
      ),
      currency: 'USD',
      ...(isNonEmptyNumber(totalOtherAmount)
        ? {
            otherAmount: (totalOtherAmount! / quantityOfInstallments).toFixed(
              2
            ),
          }
        : {}),
    }))
  }

  const generateInstallments = async () => {
    const schedule = values.flexiblePaymentType || ScheduleType.QUARTERLY

    const newTcv = tcvValue

    // compute container service length in days (if form provides containerOfferServiceLength)
    const containerServiceLength = getDaysFromServiceLength({
      containerOfferServiceLengthValue: values.containerOfferServiceLength,
    })

    const durationInMonths =
      values.serviceLength ||
      getContractDurationInMonths(
        values.agreementStartDate,
        values.subscriptionEndDate
      )

    if (schedule && schedule !== ScheduleType.CUSTOM) {
      const nextInstallments = getInstallmentsBasedOnSchedule(
        newTcv,
        durationInMonths,
        values.firstInstallmentDate || '',
        containerServiceLength
      )

      await setFieldValue('installments', nextInstallments)
      if (
        Object.prototype.hasOwnProperty.call(values, 'renewalInstallments') &&
        Object.prototype.hasOwnProperty.call(
          values,
          'renewalFlexiblePaymentType'
        )
      ) {
        const renewalStep =
          schedule === ScheduleType.MONTHLY
            ? 1
            : schedule === ScheduleType.QUARTERLY
              ? 3
              : 12
        const renewalDay = dayFromIsoDate(nextInstallments[0]?.paymentDate) || 1
        const percentages = getInstallmentPercentages(
          nextInstallments.map(installment => Number(installment.amount) || 0)
        )
        const currentRenewals =
          (values.renewalInstallments as Installments[]) || []

        await setFieldValue('renewalFlexiblePaymentType', schedule, false)
        await setFieldValue('renewalDayOfMonth', renewalDay, false)
        await setFieldValue(
          'renewalInstallments',
          nextInstallments.map((installment, index) => ({
            ...(currentRenewals[index] ||
              emptyRenewalInstallment(
                renewalDay,
                installment.currency || 'USD'
              )),
            monthsAfter: index * renewalStep,
            dayOfMonth: dayFromIsoDate(installment.paymentDate) || renewalDay,
            amount: null,
            percentage: percentages[index],
          })),
          false
        )
      }
      await setFieldTouched('installments', undefined)
      setRandomKey(getRandom())
    }
  }

  const hasCustomColumn = !!customColumns?.length

  const showDelete =
    installments.length > 1 &&
    (addButtonName === 'Add more installments'
      ? values.flexiblePaymentType === ScheduleType.CUSTOM
      : true)

  const handleChangeAmount = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const value = (event.target as HTMLInputElement).value
      await setFieldValue(
        `installments.${index}.amount`,
        value !== '' ? Number(value) : value
      )
      if (handleChangeInstallmentsAmount && installmentsRef.current) {
        const current = installmentsRef.current
        const updatedInstallments = current.map((item, i) =>
          i === index
            ? { ...item, amount: value !== '' ? Number(value) : null }
            : item
        )
        await handleChangeInstallmentsAmount(updatedInstallments)
      }
    },
    [setFieldValue, handleChangeInstallmentsAmount]
  )

  const handleChangeTotalAmount = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event?.target?.value
    if (value === '') {
      handleChange(event)
      return
    }
    if (value?.includes('.')) {
      const parts = value.split('.')
      if (parts.length === 2 && parts[1].length > 2) {
        return
      }
      handleChange(event)
      return
    }
    handleChange(event)
    return
  }

  return (
    <div
      className={classes.installmentsWrapper}
      data-testid={'installment-section'}
    >
      {addButtonName === 'Add more installments' && (
        <>
          {!!sectionDescription && sectionDescription}
          {!!sectionHeading && (
            <div className={classes.sectionHeading}>{sectionHeading}</div>
          )}
          <div className={classes.radioSelection}>
            <RadioButtonsV2
              checkedValue={{
                key: checkedValue,
                value: checkedValue,
              }}
              name="installments.flexiblePaymentType"
              radioButtonMappings={scheduleTypeOptions}
              handleChange={handleSelection}
              gapBetween={theme.spacing(2.5)}
            />
          </div>
          {values.flexiblePaymentType === ScheduleType.MONTHLY && (
            <NoteV2 description={awsMonthlyInstallmentsBannerMessage} />
          )}
          {!(values.flexiblePaymentType === ScheduleType.CUSTOM) &&
            !(hideTcvInput && hideGenerateButton) && (
              <>
                <div className={classes.resourceRow} data-testid="tcv">
                  <div className={classes.resourcesContainer}>
                    {!fixTcvView && !hideTcvInput && (
                      <div
                        className={classes.resouceField}
                        key={`installments-${randomKey}-amount`}
                      >
                        <TextInputV4
                          keyName={tcvKey}
                          title="Enter total contract value"
                          type="number"
                          placeHolder=""
                          value={get(values, tcvKey) as number}
                          updatedValue={get(values, tcvKey) as number}
                          isError={false}
                          errorText={''}
                          onChange={handleChangeTotalAmount}
                          isReadOnly={false}
                          isRequired={false}
                          showSpinner={false}
                          onBlur={handleBlur}
                          keyToPrevent={['+', '-', 'e']}
                        />
                      </div>
                    )}
                    <div
                      className={classes.resouceField}
                      key={`installments-${randomKey}-paymentDate`}
                    >
                      <DatePickerV2
                        size="small"
                        format={labraUIDateFormat}
                        views={['year', 'month', 'day']}
                        field={{
                          title: 'First Installment date',
                          humanReadableKey: 'First Installment date',
                          placeHolder: 'First Installment date',
                          id: 'firstInstallmentDate',
                          isRequired: false,
                          isReadOnly: false,
                          isCustom: false,
                          defaultValue: '',
                          dataType: 'date',
                          minDate: 'today',
                        }}
                        fieldId={'firstInstallmentDate'}
                        formik={
                          formik as unknown as FormikProps<
                            Record<string, unknown>
                          >
                        }
                        showErrorHelper={false}
                        validate
                      />
                    </div>
                    {fixTcvView && (
                      <div
                        className={clsx(
                          classes.resouceField,
                          classes.fixedTcvWrapper
                        )}
                        key={`installments-${randomKey}-amount`}
                      >
                        <DetailInfo
                          title={'Total contact for buyer'}
                          value={
                            <div
                              className={classes.price}
                              data-testid={'installment-amount-value'}
                            >
                              <span className={classes.currency}>$</span>{' '}
                              {numberFormatWithPrecision(tcvValue, 9)}
                            </div>
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
                {!hideGenerateButton && (
                  <div
                    className={classes.resourceRow}
                    data-testid="generate-installments-section"
                  >
                    <div className={classes.resourcesContainer}>
                      <div
                        className={clsx(
                          classes.resouceField,
                          classes.generateBtn
                        )}
                        key={'generateInstallments'}
                      >
                        <ButtonV2
                          styleType="outlined"
                          onClick={() => void generateInstallments()}
                          data-testid={'save-as-draft'}
                        >
                          {'Generate installments'}
                        </ButtonV2>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
        </>
      )}
      <div
        className={clsx(
          classes.root,
          !hideOuterBorder && classes.rootWithBorder,
          hideOuterBorder && classes.rootNoPadding,
          classes.elementsGap
        )}
      >
        {subHeading}

        <div className={classes.columnHeaderSection}>
          <div className={classes.titleWraper}>
            {customColumns?.map((customColumnItem, customColumnItemIndex) => (
              <Typography
                key={`${customColumnItem.keyName}-${customColumnItemIndex}`}
                className={clsx(
                  classes.sectionText,
                  classes.columnFlex,
                  !customColumnItemIndex
                    ? classes.textAlignLeft
                    : classes.textAlignRight
                )}
              >
                {customColumnItem?.header}
              </Typography>
            ))}
            <Typography
              className={clsx(
                classes.sectionText,
                classes.columnFlex,
                hasCustomColumn ? classes.textAlignRight : ''
              )}
            >
              Payment from buyer
            </Typography>
            <Typography
              className={clsx(
                classes.sectionText,
                classes.columnFlex,
                hasCustomColumn ? classes.textAlignRight : ''
              )}
            >
              Invoice date
            </Typography>
            {showDelete && <div className={classes.trashHeaderSpacer}></div>}
          </div>
          <div className={classes.border}></div>
        </div>
        <div className={classes.installmentsContent}>
          <FieldArray name="installments" key="installments">
            {({ push, remove }) => (
              <>
                {installments.map((installment, index) => {
                  const amountPath = `installments.${index}.amount`
                  const paymentDatePath = `installments.${index}.paymentDate`
                  const amountTouched = !!get(touched, amountPath, false)
                  const amountErr = get(errors, amountPath, false)
                  const amountIsError = !!(amountTouched && amountErr)
                  const amountErrorText =
                    (amountTouched &&
                      (get(errors, amountPath, '') as string)) ||
                    ''

                  const paymentDateTouched = !!get(
                    touched,
                    paymentDatePath,
                    false
                  )
                  const paymentDateErr = get(errors, paymentDatePath, '')
                  const dateShowError = !!(paymentDateTouched && paymentDateErr)
                  const dateErrorText =
                    (paymentDateTouched &&
                      (get(errors, paymentDatePath, '') as string)) ||
                    ''

                  return (
                    <InstallmentRow
                      key={`installment-row-${index}`}
                      index={index}
                      installment={installment}
                      randomKey={randomKey}
                      amountIsError={amountIsError}
                      amountErrorText={amountErrorText}
                      dateShowError={dateShowError}
                      dateErrorText={dateErrorText}
                      showDelete={showDelete}
                      columnDivision={columnDivision}
                      customColumns={customColumns}
                      classes={classes}
                      formik={formik}
                      onAmountChange={handleChangeAmount}
                      handleBlur={handleBlur}
                      onRemove={remove}
                      validate={validate}
                    />
                  )
                })}
                {installmentsLength <= installmentsLimit - 1 &&
                (addButtonName === 'Add more installments'
                  ? values.flexiblePaymentType === ScheduleType.CUSTOM
                  : true) ? (
                  <div>
                    <ButtonV2
                      className={classes.widthButton}
                      styleType="link"
                      onClick={() => {
                        const lastDate =
                          installments[installments.length - 1]?.paymentDate
                        push({
                          ...EMPTY_INSTALLMENT,
                          paymentDate: nextMonthSameDay(lastDate),
                        })
                      }}
                      data-testid="add-a-installments-button"
                    >
                      {addButtonName}
                    </ButtonV2>
                    <Typography className={classes.buttonSubText}>
                      You can add{' '}
                      {installmentsLimit -
                        (installmentsLength === 0
                          ? 1
                          : installmentsLength)}{' '}
                      more {buttonCounterName}.
                    </Typography>
                  </div>
                ) : null}
              </>
            )}
          </FieldArray>
        </div>
      </div>
      {shouldShowTotal && installmentsLength > 0 && (
        <div
          className={clsx(
            classes.root,
            classes.rootWithBorder,
            classes.lowerWrapper,
            classes.metricsRow
          )}
          data-testid="installment-metrics"
          key="installmentMetrics"
        >
          <div className={classes.metricItem} data-testid="total-price">
            <Typography className={classes.sectionText}>
              Total amount
            </Typography>
            <Typography className={classes.totalText} data-testid="price">
              {getCurrencySymbol(values?.currency as string) || '$'}
              {totalAmount}
            </Typography>
          </div>
          <div className={classes.metricItem} data-testid="installment-count">
            <Typography className={classes.sectionText}>
              Number of installments
            </Typography>
            <Typography
              className={classes.totalText}
              data-testid="installment-count-value"
            >
              {installmentsLength}
            </Typography>
          </div>
        </div>
      )}
    </div>
  )
}
